import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useAsync, useAsyncCallback } from 'react-async-hook';
import { debounce, merge } from 'lodash';
import { CarStatus } from '@upstart/harmony/lib/constants';
import widgetsPreviewImage from '../../../assets/guides/widgets-preview.png';
import { DealerLaunchStepProps } from '../types';
import { Button } from '../../Input/Button/Button';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { TextField } from '../../Input/TextField/TextField';
import { PageWidgetConfig, WidgetConfig } from '../../../types/widgetTypes';
import WidgetService from '../../../services/WidgetService';
import { Spinner } from '../../Spinner/Spinner';
import { ErrorMessage } from '../../ErrorMessage/ErrorMessage';
import { ColorPicker } from '../../Input/ColorPicker/ColorPicker';
import { colorValidator, triggerValidation } from '../../../utils/validation';
import { Slider } from '../../Input/Slider/Slider';
import { PreviewGuide } from '../../QuickGuide/QuickGuide';
import { DealerLaunchFormCardLayout } from '../DealerLaunchFormCardLayout';
import styles from '../styles.module.scss';

// Preview Data for the widgets
const PREVIEW_CAR = {
  id: '4T1C31AK9LU527263',
  msrp: 15299,
  dealer_id: 'victorytoyota',
  status: CarStatus.AVAILABLE,
};

const mergePageConfig = (
  savedPageConfig: PageWidgetConfig | undefined,
  updatedPageConfig: Partial<PageWidgetConfig> | undefined,
) =>
  merge({}, savedPageConfig, updatedPageConfig, {
    actionBackgroundColor:
      updatedPageConfig?.leadCaptureButton?.backgroundColor ||
      savedPageConfig?.actionBackgroundColor,
    actionRadius:
      updatedPageConfig?.leadCaptureButton?.radius ??
      savedPageConfig?.actionRadius,
  });

export const DealerLaunchOnlineWidgets: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  prevStep,
  nextStep,
}) => {
  const form = useLaunchpadForm<WidgetConfig>();
  const formRef = useRef(form);
  formRef.current = form;
  const { isValid: isFormValid } = form.formState;
  const formData = form.watch();
  // get the current widget config for the dealer
  const widgetConfig = useAsync(WidgetService.getConfig, [dealer.id], {
    // reset the form when the data gets loaded
    onSuccess: ({ data }) =>
      requestAnimationFrame(() => {
        formRef.current.reset(data);
        triggerValidation(formRef.current, 'config.VDP.leadCaptureButton.text');
        triggerValidation(
          formRef.current,
          'config.VDP.leadCaptureButton.backgroundColor',
        );
      }),
  });

  // load a button preview when the config changes
  const buttonGroupsPreview = useAsyncCallback(
    WidgetService.getButtonGroupsPreview,
  );
  const buttonGroupsPreviewFetchDebounced = useRef(
    debounce(buttonGroupsPreview.execute, 500),
  );
  useEffect(() => {
    if (widgetConfig.result && isFormValid) {
      buttonGroupsPreviewFetchDebounced.current({
        car: PREVIEW_CAR,
        config: mergePageConfig(
          widgetConfig.result.data.config.VDP,
          formData.config.VDP,
        ),
        dealer,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetConfig.result, JSON.stringify(formData), isFormValid]);

  const previewIframe = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIFrameHeight] = useState<number>();
  const applyPreview = () => {
    // get the preview containers
    const cssContainer = previewIframe.current?.contentDocument?.getElementById(
      'css-container',
    );
    const previewContainer = previewIframe.current?.contentDocument?.getElementById(
      'preview-container',
    );
    if (!cssContainer || !previewContainer || !buttonGroupsPreview.result)
      return;

    // update the preview containers with the preview data
    cssContainer.innerHTML = buttonGroupsPreview.result.data.CSS || '';
    previewContainer.innerHTML =
      buttonGroupsPreview.result.data.buttonGroups[PREVIEW_CAR.id] || '';

    // set the iframe height based on the scroll height of the content
    setIFrameHeight(previewIframe.current?.contentDocument?.body.scrollHeight);
  };

  // every time the button group preview data changes, update the iframe
  useEffect(() => {
    if (buttonGroupsPreview.result) applyPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonGroupsPreview.result]);

  const saveWidgetConfig = useAsyncCallback(WidgetService.updateConfig);
  const onSubmit = async (data: Partial<WidgetConfig>) => {
    const update = merge({}, widgetConfig.result?.data, data);
    // all edits are made to the VDP, so resolve the VDP changes, then copy them to the SRP
    update.config.VDP = mergePageConfig(
      widgetConfig.result?.data.config.VDP,
      data.config?.VDP,
    );
    update.config.SRP = update.config.VDP;

    await saveWidgetConfig.execute(dealer.id, update);
    await nextStep({ launchpad: { status: { finishedWidgetConfig: true } } });
  };

  return (
    <DealerLaunchFormCardLayout
      className={styles.widgetsStep}
      form={form}
      title="Design Your Buttons"
      subTitle={
        <>
          The buttons are the primary way that Upstart Auto Retail will
          integrate with your website.
          <br />
          Customers will enter the Upstart Auto Retail tool directly from these
          buttons.
        </>
      }
      cardTitle="Dealer Information"
      prevStep={prevStep}
      onFormSubmit={onSubmit}
    >
      {widgetConfig.loading && <Spinner variant="fullscreen" />}
      <ErrorMessage
        error={
          widgetConfig.error ||
          buttonGroupsPreview.error ||
          saveWidgetConfig.error
        }
      />
      <div className="flex-row">
        <h1>What would you like the main Call to Action to say?</h1>
        <div className="spacer" />
        <PreviewGuide
          action="Show Where This is Used"
          guideTitle="Where are the widgets used?"
        >
          <div>
            The widgets are designed to integrate with your existing dealership
            website. They are applied to your products where your customers can
            browse vehicles.
            <img src={widgetsPreviewImage} alt="Website Preview" />
            Please note that this is not a live preview.
          </div>
        </PreviewGuide>
      </div>
      <TextField
        label="Main CTA"
        name="config.VDP.leadCaptureButton.text"
        tooltip="The main button customers use to launch Upstart Auto Retail from your website."
        width="md"
        maxLength={25}
        form={form}
        validation={{ required: true }}
      />
      <h1>Customize your widgets</h1>

      <div className={styles.stepRow}>
        <div className={styles.widgetInputContainer}>
          <ColorPicker
            label="Select Custom Color"
            tooltip="Upstart Auto Retail recommends matching a color on your current website."
            name="config.VDP.leadCaptureButton.backgroundColor"
            width="md"
            form={form}
            validation={{ required: true, pattern: colorValidator }}
          />
          <Slider
            label="Button Corner Radius"
            name="config.VDP.leadCaptureButton.radius"
            form={form}
            max={20}
            min={0}
            marks={{ 0: <></>, 5: <></>, 10: <></>, 15: <></>, 20: <></> }}
          />
          <Button
            label="Reset Brand Defaults"
            variant="minimal"
            onClick={() => {
              form.setValue(
                'config.VDP.leadCaptureButton.text',
                'Get Our Best Price',
              );
              form.setValue(
                'config.VDP.leadCaptureButton.backgroundColor',
                '#5bc0de',
              );
              form.setValue('config.VDP.leadCaptureButton.radius', 0);
            }}
          />
        </div>
        <div className={styles.widgetPreviewContainer}>
          {buttonGroupsPreview.loading && (
            <Spinner variant="fill-container-overlay" />
          )}
          <span className={styles.widgetPreviewLabel}>Widget Preview</span>
          <iframe
            ref={previewIframe}
            title="Widget Preview"
            className={styles.widgetPreview}
            style={{ height: iframeHeight }}
            onLoad={applyPreview}
            sandbox="allow-scripts allow-same-origin"
            srcDoc={`
                <html>
                  <head>
                    <script
                      id="prodigyButtonGroupAgent"
                      src="https://cdn.getprodigy.com/widgets/priceButtonGroupAgent.v1.0.js"
                    ></script>
                    <style type="text/css">
                      body {
                        margin: 0;
                        overflow: hidden;
                      }
                      .prodigy-label-container {
                        display: none !important;
                      }
                      .prodigy-msg {
                        display: none !important;;
                      }
                    </style>
                  </head>
                  <body>
                    <div id="css-container">
                    </div>
                    <div class="prodigy-pricing-button-group">
                      <div id="preview-container">
                      </div>
                    </div>
                  </body>
                </html>
            `}
          />
        </div>
      </div>
    </DealerLaunchFormCardLayout>
  );
};
